import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';

/**
 * Cartão de ativação usado acima de uma aba, de uma seção ou de uma sub-seção.
 * Concentra o toggle de ativação (que por isso sai do cabeçalho da seção) e, enquanto
 * estiver inativo, exibe o estado vazio padrão no lugar do conteúdo — o chamador só
 * renderiza os campos quando o controle estiver ativo.
 *
 * As descrições padrão são voltadas às abas de tributo, que são a maioria dos usos:
 * em outros contextos informe descricaoAtivo e descricaoInativo.
 */
@Component({
    selector: 'app-ativacao-section-card',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatSlideToggleModule, AlertCardComponent, EmptyStateComponent],
    templateUrl: './ativacao-section-card.component.html',
    styleUrl: './ativacao-section-card.component.css',
})
export class AtivacaoSectionCardComponent {
    @Input({ required: true }) control!: FormControl;

    /** Nome do tributo, da seção ou da sub-seção controlada por este cartão. */
    @Input() titulo: string = '';

    /**
     * Em seções e sub-seções o nome já aparece no cabeçalho da seção, então o alerta
     * fica apenas com a descrição e o toggle para não repetir o título.
     */
    @Input() exibirTituloNoAlerta: boolean = true;

    @Input() descricaoAtivo: string = 'Ativo. As configurações abaixo serão aplicadas nos cálculos fiscais.';
    @Input() descricaoInativo: string = 'Inativo. Ative para configurar e aplicar nos cálculos fiscais.';

    /** Estado vazio exibido no lugar do conteúdo enquanto o controle estiver inativo. */
    @Input() emptyStateIcone: string = 'toggle_off';
    @Input() emptyStateTitulo: string = '';
    @Input() emptyStateDescricao: string = '';

    /**
     * Emitido apenas quando o usuário liga/desliga o toggle, para efeitos colaterais
     * como limpar campos dependentes. Não dispara em patchValue do carregamento.
     */
    @Output() ativacaoChange = new EventEmitter<boolean>();

    public get isAtivo(): boolean {
        return !!this.control?.value;
    }

    public get tituloAlerta(): string {
        return this.exibirTituloNoAlerta ? this.titulo : '';
    }

    public get emptyStateTituloTexto(): string {
        return this.emptyStateTitulo || `${this.titulo} desativado`;
    }

    public get emptyStateDescricaoTexto(): string {
        return this.emptyStateDescricao || 'Ative no cartão acima para exibir e configurar os campos.';
    }
}
